/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */
import PageableCollection from '@/backbone/PageableCollection';
import ButtonCell from '@/backgrid/ButtonCell';
import StringCell from '@/backgrid/StringCell';
import LinkCell from '@/backgrid/LinkCell';
import { DateCell } from '@/backgrid/MomentCell';
import GridView from '@/views/GridView';
import showModal from '@/util/showModal';

import HideNotification from '../../services/HideNotification';
import MembershipApprovalDetailsModalView from '../MembershipApprovalDetailsModalView/MembershipApprovalDetailsModalView';

const NotificationApprovalGridView = GridView.extend({
  initialize() {
    const self = this;
    this.options.columns = [
      {
        name: 'date',
        label: 'Date',
        cell: DateCell,
        headerCell: 'custom',
        width: 10,
        editable: false,
        direction: 'ascending'
      },
      {
        name: 'type',
        label: 'Type',
        cell: StringCell,
        headerCell: 'custom',
        width: 16,
        editable: false
      },
      {
        name: 'command',
        label: 'Command',
        cell: LinkCell.extend({
          href(model) {
            return `#command/${model.get('commandId')}`;
          },
          name() {
            return this.model.get('command');
          },
          newTab: true
        }),
        headerCell: 'custom',
        width: 16,
        editable: false
      },
      {
        name: 'itemDescription',
        label: 'Item',
        cell: LinkCell.extend({
          textTemplate(model) {
            const itemDescription = model.get('itemDescription');
            const orderNumber = self.getNotificationOrderNumber(itemDescription)
            if (orderNumber) {
              const template = itemDescription.replace(orderNumber, "{link}");
              return template;
            } else {
              return itemDescription;
            }
          },

          href(model) {
            const itemDescription = model.get('itemDescription');
            const orderNumber = self.getNotificationOrderNumber(itemDescription)
            if (orderNumber) {
              const orderId = orderNumber.substring(1);
              return `#order/${orderId}`;
            } else {
              return null;
            }
          },

          name() {
            const itemDescription = this.model.get('itemDescription');
            const orderNumber = self.getNotificationOrderNumber(itemDescription)
            return orderNumber || this.model.get(this.column.get('name'));
          },
          newTab: true
        }),
        headerCell: 'custom',
        width: 50,
        editable: false
      },
      {
        name: 'notificationButton',
        label: '',
        width: 8,
        headerCell: 'custom',
        cell: ButtonCell.extend({
          name() {
            return this.model.get('type') === 'Notification' ? 'Hide' : 'Details';
          },
          onClick(e) {
            this.model.get('type') === 'Notification' ? self.onClickHide(e, this) : self.openDetailsModal(e, this);
          },
          buttonClass: 'btn-sm btn-outline-primary text-center'
        }),
        editable: false,
        sortable: false
      }
    ];
    const notifications = this.model.get('notificationsAndApprovals');
    this.options.collection = new PageableCollection(notifications, { pageSize: 10 });

    this.options.emptyText = 'No Notifications or Approvals';
  },

  async onClickHide(e, self) {
    e.preventDefault();
    const notificationID = self.model.get('id');

    try {
      await HideNotification(notificationID);
      this.options.collection.remove(self.model);
    } catch (err) {
      throw new Error('Unable to hide notification.');
    }
  },

  async openDetailsModal(e, self) {
    if (e?.type) {
      e.preventDefault();
    }

    const model = await showModal({
      title: 'Membership Approval Details',
      view: new MembershipApprovalDetailsModalView({
        model: self.model
      }),
      dialogClasses: 'modal-lg'
    });

    this.options.collection.remove(model);
  },

  getNotificationOrderNumber(itemDescription){
    if (!itemDescription.includes('You have a new customer order') && !itemDescription.includes('You have a new relocation request')) {
      return null;
    }

    const matches = itemDescription.match(/#\d+/)
    return matches ? matches[0] : null;
  }
});

export default NotificationApprovalGridView;
